import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Plus, Trash2, GripVertical, Pencil, EyeOff } from "lucide-react";

import {
  AdminPage,
  ImageField,
  Loading,
  SortableGrid,
  SortableGridItem,
  useRows,
  type Row,
} from "@/components/admin/AdminKit";
import { RichText } from "@/components/admin/RichText";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/admin/speakeri")({
  component: SpeakersAdmin,
});

const GROUPS = [
  { key: "speaker", label: "Speakeri" },
  { key: "moderator_conf", label: "Moderatori Conferință" },
  { key: "moderator_gala", label: "Moderatori Gală" },
] as const;

type FormState = {
  id?: string;
  name: string;
  role_title: string;
  company: string;
  bio: string;
  photo_url: string;
  category: string;
  visible: boolean;
  sort_order: number;
};

function emptyForm(category: string, sort_order: number): FormState {
  return {
    name: "",
    role_title: "",
    company: "",
    bio: "",
    photo_url: "",
    category,
    visible: true,
    sort_order,
  };
}

function SpeakersAdmin() {
  const { rows, isLoading, save, remove, reorder } = useRows("speakers");
  const [editing, setEditing] = useState<FormState | null>(null);

  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: rows.filter((r) =>
          g.key === "speaker"
            ? String(r["category"] ?? "speaker") === "speaker"
            : r["category"] === g.key,
        ),
      })),
    [rows],
  );

  function openNew(category: string) {
    setEditing(emptyForm(category, rows.length));
  }

  function openEdit(item: Row) {
    setEditing({
      id: item.id,
      name: String(item["name"] ?? ""),
      role_title: String(item["role_title"] ?? ""),
      company: String(item["company"] ?? ""),
      bio: String(item["bio"] ?? ""),
      photo_url: String(item["photo_url"] ?? ""),
      category: String(item["category"] ?? "speaker"),
      visible: Boolean(item["visible"]),
      sort_order: Number(item["sort_order"] ?? 0),
    });
  }

  async function handleSave() {
    if (!editing) return;
    const ok = await save({ ...editing });
    if (ok) setEditing(null);
  }

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Speakeri & moderatori"
      description="Trage cardurile pentru a le reordona. Click pe card pentru editare."
      action={
        <Button onClick={() => openNew("speaker")}>
          <Plus className="mr-2 h-4 w-4" /> Adaugă speaker
        </Button>
      }
    >
      <div className="space-y-12">
        {groups.map((group) => (
          <section key={group.key} className="space-y-4">
            <div className="flex items-center justify-between gap-4 border-b border-border pb-2">
              <h2 className="font-display text-lg font-bold">
                {group.label}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  ({group.items.length})
                </span>
              </h2>
              <Button size="sm" variant="outline" onClick={() => openNew(group.key)}>
                <Plus className="mr-2 h-4 w-4" /> Adaugă
              </Button>
            </div>

            {group.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">Niciun element încă.</p>
            ) : (
              <SortableGrid
                items={group.items}
                onReorder={(ids) => {
                  // reordonăm doar în cadrul grupului, păstrând poziția globală
                  const groupIds = group.items.map((i) => i.id);
                  const globalIds = rows.map((r) => r.id);
                  let cursor = 0;
                  const next = globalIds.map((id) =>
                    groupIds.includes(id) ? ids[cursor++]! : id,
                  );
                  reorder(next);
                }}
              >
                {(item) => (
                  <SortableGridItem key={item.id} id={item.id}>
                    {({ attributes, listeners }) => (
                      <div className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition hover:shadow-md">
                        <button
                          type="button"
                          aria-label="Reordonează"
                          className="absolute left-2 top-2 z-10 cursor-grab rounded-md bg-background/85 p-1 text-muted-foreground backdrop-blur active:cursor-grabbing"
                          {...(attributes as object)}
                          {...(listeners as object)}
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                        {!item["visible"] && (
                          <span className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded-md bg-background/85 px-2 py-1 text-[11px] text-muted-foreground backdrop-blur">
                            <EyeOff className="h-3 w-3" /> ascuns
                          </span>
                        )}
                        <div className="aspect-square w-full overflow-hidden bg-muted">
                          {item["photo_url"] ? (
                            <img
                              src={String(item["photo_url"])}
                              alt={String(item["name"] ?? "")}
                              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center font-display text-3xl text-muted-foreground">
                              {String(item["name"] ?? "?").charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col gap-1 p-3">
                          <p className="line-clamp-1 font-semibold">{String(item["name"] ?? "")}</p>
                          <p className="line-clamp-2 text-xs text-muted-foreground">
                            {String(item["role_title"] ?? "")}
                          </p>
                          <div className="mt-auto flex gap-2 pt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => openEdit(item)}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label="Șterge"
                              onClick={() => {
                                if (window.confirm(`Ștergi ${String(item["name"] ?? "")}?`))
                                  void remove(item.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </SortableGridItem>
                )}
              </SortableGrid>
            )}
          </section>
        ))}
      </div>

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editează" : "Adaugă"} persoană</DialogTitle>
            <DialogDescription>
              Datele apar pe pagina publică de speakeri și în modalul de bio.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nume</Label>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Funcție</Label>
                  <Input
                    value={editing.role_title}
                    onChange={(e) => setEditing({ ...editing, role_title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Companie / instituție</Label>
                  <Input
                    value={editing.company}
                    onChange={(e) => setEditing({ ...editing, company: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categorie</Label>
                  <Select
                    value={editing.category}
                    onValueChange={(v) => setEditing({ ...editing, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GROUPS.map((g) => (
                        <SelectItem key={g.key} value={g.key}>
                          {g.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ImageField
                label="Fotografie"
                cropable
                value={editing.photo_url}
                onChange={(url) => {
                  setEditing((f) => (f ? { ...f, photo_url: url } : f));
                  // persistăm imediat noua imagine / decupaj pentru rândurile existente
                  if (editing.id) void save({ id: editing.id, photo_url: url });
                }}
              />


              <RichText
                label="Biografie"
                value={editing.bio}
                onChange={(html) => setEditing((f) => (f ? { ...f, bio: html } : f))}
              />

              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editing.visible}
                  onCheckedChange={(v) => setEditing({ ...editing, visible: v })}
                />
                Vizibil pe site
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Anulează
            </Button>
            <Button onClick={handleSave}>Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
